import React from "react";
import { motion } from "framer-motion";
import Visit1 from "@/assets/images/visit1.jpg";
import Visit2 from "@/assets/images/visit2.jpg";
import Visit3 from "@/assets/images/visit3.jpg";
import AnnaImg from "@/assets/images/anna-t2.jpg";
import { PageHeader } from "../components/pageheader";
import SectionTitle from "../components/sectiontitle";
import CTAButton from "../components/button";

/* Container */
const Container: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className = "",
  children,
}) => (
  <div className={`mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 ${className}`}>
    {children}
  </div>
);

/* Hire Page */
export default function HirePage() {
  return (
    <section className="py-14 text-white">
      <Container>
        <PageHeader
          eyebrow="Contact"
          title="Get in touch"
          text="We’re a friendly bunch who love talking about digital products, design systems, and complex platforms — whether on a call, over coffee in our Malmö studio, or at your place."
        />
        {/* ---------- Personal intro (Anna as front figure) ---------- */}
        <div className="mt-20 mb-40">
          <SectionTitle title="Let's talk about your project" />

          {/* 1 kolumn mobilt, 2 kolumner från md+. Vänster 360px, höger flex-1 */}
          <div className="flex flex-col md:flex-row md:flex-nowrap gap-12 md:gap-20 items-start mt-8">
            {/* Portrait */}
            <div className="w-full md:w-[360px] shrink-0">
              <img
                src={AnnaImg}
                alt="Anna Tunberg"
                className="w-full h-auto object-cover object-top rounded-lg"
                loading="lazy"
              />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0 text-left">
              <p className="text-white/90 text-lg mb-3">
                Hi, I’m{" "}
                <span className="font-semibold text-white">Anna Tunberg</span>{" "}
                — Head of Operations here at BoldBrave.
              </p>

              <p className="text-white/70 mb-4 leading-relaxed">
                I spend most of my days helping teams and brands turn complex
                ideas into clear plans. If you’re unsure where to start, that’s
                perfectly fine — that’s where I come in. <br />
                Let’s talk scope, direction, or just what’s next for your
                digital world.
              </p>

              <CTAButton
                href="mailto:anna@bold-n-brave.com"
                className="px-6 py-2.5 mb-4"
              >
                Send me an email
              </CTAButton>
              <p className="mt-3 text-sm text-white/50">
                Email BoldBrave about your challenges — we’re always up for a
                chat or call us on +46739575520
              </p>
            </div>
          </div>
        </div>

        {/* ---------- Visit section ---------- */}
        <SectionTitle title="Come over to our place" />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[Visit1, Visit2, Visit3].map((img, i) => (
            <motion.img
              key={i}
              src={img}
              loading="lazy"
              alt={`Visit ${i + 1}`}
              className="w-full h-auto rounded-lg object-cover"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
            />
          ))}
        </div>

        {/* Google Maps link */}
        <a
          href="https://www.google.com/maps/place/Genetor+Malm%C3%B6+C/@55.6065841,12.9999847,17z"
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-6 text-[#FF893F] hover:text-white transition text-left"
        >
          Find us on Google Maps
        </a>

        {/* ---------- Visit details block (under the Google Maps link) ---------- */}
        <div className="mt-8 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-[1.3fr_1fr]">
          {/* Left: directions / copy */}
          <div>
            <p className="text-white/80">
              There’s nothing like meeting face-to-face.
            </p>
            <p className="mt-4 text-white/70">
              It’s a pleasant 3-minute walk from Malmö Central to our studio.
              Head south towards the harbour front and keep the water on your
              right — we’re inside the Genetor Malmö C building. If you’re
              coming by bike, we’re bike-friendly and there’s indoor parking for
              visitors.
            </p>
            <p className="mt-4 text-white/70">
              Driving? There are several car parks within a short walk of the
              studio. Arriving by train or bus? Malmö Central is our closest
              hub.
            </p>
          </div>

          {/* Right: address / meta */}
          <div className=" md:p-5">
            <h3 className="text-lg font-semibold">BoldBrave AB</h3>
            <p className="mt-1 text-white/80">C/O Genetor AB, Lokgatan 13G</p>
            <p className="text-white/60">Malmö</p>
            <p className="text-white/60">Sweden</p>

            {/* what3words – byt href/text om ni har en exakt länk */}
            <a
              href="https://w3w.co/dripped.position.pricier"
              className="mt-4 inline-block text-[#FF893F] hover:underline"
              aria-label="Open what3words location"
            >
              ///dripped.position.pricier
            </a>
          </div>
        </div>
      </Container>
    </section>
  );
}
